import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import type { InitOptions } from 'i18next';
import { supabase } from '@flows.cash/core';

// Extend the i18n interface to include our custom methods
declare module 'i18next' {
  interface i18n {
    syncFromDatabase: (userId: string) => Promise<string | undefined>;
  }
}

// Import translations
import enCommon from './locales/en/common.json';
import trCommon from './locales/tr/common.json';
import arCommon from './locales/ar/common.json';
import esCommon from './locales/es/common.json';
import frCommon from './locales/fr/common.json';
import deCommon from './locales/de/common.json';

const resources = {
  en: {
    common: enCommon,
  },
  tr: {
    common: trCommon,
  },
  ar: {
    common: arCommon,
  },
  es: {
    common: esCommon,
  },
  fr: {
    common: frCommon,
  },
  de: {
    common: deCommon,
  },
};

// Get stored language from database or localStorage
const getStoredLanguage = () => {
  // Default to English if no stored preference
  const storedLang = localStorage.getItem('i18nextLng');
  return storedLang || 'en';
};

// Initialize with basic configuration first
const i18nConfig: InitOptions = {
  resources,
  defaultNS: 'common',
  lng: getStoredLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  detection: {
    order: ['localStorage', 'navigator'],
    caches: ['localStorage'],
    lookupLocalStorage: 'i18nextLng',
  },
  supportedLngs: ['en', 'tr', 'ar', 'es', 'fr', 'de'],
  load: 'languageOnly',
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(i18nConfig)
  .then(() => {
    // Set initial RTL/LTR direction based on the loaded language
    const currentLang = i18n.language;
    document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';
    
    // Try to get the user ID from localStorage or any auth storage
    const getLoggedInUserId = () => {
      try {
        // Check for auth storage
        const authStorageStr = localStorage.getItem('auth-storage');
        if (authStorageStr) {
          const authStorage = JSON.parse(authStorageStr);
          if (authStorage.state?.user?.id) {
            return authStorage.state.user.id;
          }
        }
        return null;
      } catch (e) {
        console.error('Error getting user ID from storage:', e);
        return null;
      }
    };

    // If user is logged in, check their language preference in the database
    const userId = getLoggedInUserId();
    if (userId) {
      console.log('Found logged in user, checking language preference in database');
      
      // Immediately invoke async function to properly handle promise errors
      (async () => {
        try {
          // Get language from user_preferences table
          const { data, error } = await supabase
            .from('user_preferences')
            .select('language')
            .eq('id', userId)
            .single();
            
          if (error) {
            console.error('Error fetching language from user_preferences:', error);
            return;
          }
          
          if (data?.language && data.language !== i18n.language) {
            console.log(`Database language preference found: ${data.language}, changing from ${i18n.language}`);
            await i18n.changeLanguage(data.language);
            
            // After language change, update document direction if needed
            document.documentElement.dir = data.language === 'ar' ? 'rtl' : 'ltr';
            
            // Force update localStorage to match database preference
            localStorage.setItem('i18nextLng', data.language);
          }
        } catch (err) {
          console.error('Error getting user language preference:', err);
        }
      })();
    }
  });

// Add a method to synchronize language settings from the database
i18n.syncFromDatabase = async (userId: string) => {
  if (!userId) return;
  
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('language')
      .eq('id', userId)
      .single();
      
    if (error) {
      console.error('Error fetching language from database:', error);
      return;
    }
    
    if (data?.language && data.language !== i18n.language) {
      console.log(`Syncing language from database: ${data.language}`);
      await i18n.changeLanguage(data.language);
      
      // Update document direction
      document.documentElement.dir = data.language === 'ar' ? 'rtl' : 'ltr';
      
      // Update localStorage to match
      localStorage.setItem('i18nextLng', data.language);
      
      return data.language;
    }
  } catch (err) {
    console.error('Error synchronizing language from database:', err);
  }
  
  return i18n.language;
};

export default i18n;
